<template>
  <transition name="ve-help">
    <div v-if="visible" class="ve-help" @click.self="$emit('close')">
      <aside class="ve-help__panel" role="dialog" aria-labelledby="ve-help-title">
        <header class="ve-help__header">
          <div>
            <h2 id="ve-help-title" class="ve-help__title">{{ t('title') }}</h2>
            <p class="ve-help__subtitle" v-html="fmt(t('subtitle'))"></p>
          </div>
          <button
            ref="closeBtn"
            type="button"
            class="ve-help__close"
            :aria-label="$t('actions.close')"
            @click="$emit('close')"
          >
            <BaseIcon name="x" />
          </button>
        </header>

        <div class="ve-help__body">
          <!-- Section 1: Three modes -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('modes-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('modes-intro'))"></p>
            <table class="ve-help__table">
              <thead>
                <tr>
                  <th>{{ t('column-shape') }}</th>
                  <th>{{ t('column-behavior') }}</th>
                  <th>{{ t('column-example') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in modeRows" :key="row.id">
                  <td v-html="fmt(row.shape)"></td>
                  <td v-html="fmt(row.behavior)"></td>
                  <td>
                    <code v-for="(ex, i) in row.examples" :key="i" class="ve-help__code">{{ ex }}</code>
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <!-- Section 2: Available context keys -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('context-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('context-intro'))"></p>
            <table class="ve-help__table">
              <thead>
                <tr>
                  <th>{{ t('column-key') }}</th>
                  <th>{{ t('column-notes') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in contextRows" :key="row.id">
                  <td>
                    <code class="ve-help__code">{{ row.key }}</code>
                    <span v-if="row.bcOnly" class="ve-help__badge">{{ t('bc-only-badge') }}</span>
                  </td>
                  <td v-html="fmt(row.notes)"></td>
                </tr>
              </tbody>
            </table>
            <p class="ve-help__note" v-html="fmt(t('context-note'))"></p>
          </section>

          <!-- Section 3: Path navigation -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('paths-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('paths-intro'))"></p>
            <pre class="ve-help__code-block">{{ pathExamples }}</pre>
          </section>

          <!-- Section 4: Array operators -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('arrays-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('arrays-intro'))"></p>
            <table class="ve-help__table">
              <thead>
                <tr>
                  <th>{{ t('column-syntax') }}</th>
                  <th>{{ t('column-behavior') }}</th>
                  <th>{{ t('column-example') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in arrayRows" :key="row.id">
                  <td>
                    <code class="ve-help__code">{{ row.syntax }}</code>
                  </td>
                  <td v-html="fmt(row.behavior)"></td>
                  <td>
                    <code class="ve-help__code">{{ row.example }}</code>
                  </td>
                </tr>
              </tbody>
            </table>
            <p class="ve-help__note" v-html="fmt(t('arrays-note'))"></p>
          </section>

          <!-- Section 5: Value transformations -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('operators-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('operators-intro'))"></p>
            <table class="ve-help__table ve-help__table--operators">
              <thead>
                <tr>
                  <th>{{ t('column-operator') }}</th>
                  <th>{{ t('column-applies-to') }}</th>
                  <th>{{ t('column-example') }}</th>
                  <th>{{ t('column-result') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in operatorRows" :key="row.id">
                  <td>
                    <code class="ve-help__code">{{ row.operator }}</code>
                  </td>
                  <td>{{ row.appliesTo }}</td>
                  <td>
                    <code class="ve-help__code">{{ row.example }}</code>
                  </td>
                  <td v-html="fmt(row.result)"></td>
                </tr>
              </tbody>
            </table>
            <p class="ve-help__note" v-html="fmt(t('operators-required-note'))"></p>
            <p class="ve-help__note" v-html="fmt(t('operators-requiredAny-note'))"></p>
            <p class="ve-help__note" v-html="fmt(t('operators-nullIfRange-note'))"></p>
          </section>

          <!-- Section 6: Cookbook -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('cookbook-title') }}</h3>
            <p class="ve-help__intro" v-html="fmt(t('cookbook-intro'))"></p>
            <dl class="ve-help__cookbook">
              <template v-for="row in cookbookRows">
                <dt :key="`${row.id}-dt`">
                  {{ row.pattern }}
                  <span v-if="row.bcOnly" class="ve-help__badge">{{ t('bc-only-badge') }}</span>
                </dt>
                <dd :key="`${row.id}-dd`">
                  <pre class="ve-help__code-block">{{ row.expression }}</pre>
                  <p v-if="row.explainer" class="ve-help__explainer" v-html="fmt(row.explainer)"></p>
                </dd>
              </template>
            </dl>
          </section>

          <!-- Section 7: Known limitations -->
          <section class="ve-help__section">
            <h3 class="ve-help__section-title">{{ t('limits-title') }}</h3>
            <table class="ve-help__table">
              <thead>
                <tr>
                  <th>{{ t('column-limitation') }}</th>
                  <th>{{ t('column-workaround') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in limitRows" :key="row.id">
                  <td v-html="fmt(row.limitation)"></td>
                  <td v-html="fmt(row.workaround)"></td>
                </tr>
              </tbody>
            </table>
          </section>
        </div>
      </aside>
    </div>
  </transition>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon';

export default {
  name: 'ValueExpressionHelpDrawer',
  components: { BaseIcon },
  props: {
    visible: { type: Boolean, default: false },
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        // Save current focus and move it into the drawer (a11y + Tab-cycle behaves naturally).
        this._prevFocus = document.activeElement;
        this.$nextTick(() => {
          const target = this.$refs.closeBtn;
          if (target && typeof target.focus === 'function') target.focus();
        });
        // Document-level ESC handler in capture phase. Two problems this solves:
        //   1. A drawer-level @keydown.esc would miss events when focus is on body
        //      (e.g. after clicking non-focusable content inside the drawer like a
        //      <p> or <code>). Document-level catches them regardless of focus.
        //   2. stopPropagation in capture phase prevents the event from ever reaching
        //      Vuetify's v-dialog ESC handler on the surrounding field-mapping dialog,
        //      so the parent dialog doesn't also close.
        this._onDocKeydown = (e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            e.preventDefault();
            this.$emit('close');
          }
        };
        document.addEventListener('keydown', this._onDocKeydown, true);
      } else {
        if (this._onDocKeydown) {
          document.removeEventListener('keydown', this._onDocKeydown, true);
          this._onDocKeydown = null;
        }
        if (this._prevFocus && typeof this._prevFocus.focus === 'function') {
          this._prevFocus.focus();
          this._prevFocus = null;
        }
      }
    },
  },
  beforeDestroy() {
    if (this._onDocKeydown) document.removeEventListener('keydown', this._onDocKeydown, true);
  },
  computed: {
    pathExamples() {
      // Pair each expression with a translated description, then pad the expressions
      // to a uniform width so the `→` arrows line up regardless of how long the
      // translated description is.
      const items = [
        { expr: '{{opportunity.name}}', desc: this.t('path-example-opportunity-name') },
        { expr: '{{tender.title}}', desc: this.t('path-example-tender-title') },
        { expr: '{{tender.value.amount}}', desc: this.t('path-example-nested') },
        { expr: '{{tender.source.url}}', desc: this.t('path-example-multi-level') },
      ];
      const maxExprLen = items.reduce((m, i) => Math.max(m, i.expr.length), 0);
      return items.map((i) => `${i.expr}${' '.repeat(maxExprLen - i.expr.length + 2)}→ ${i.desc}`).join('\n');
    },
    modeRows() {
      return [
        {
          id: 'static',
          shape: this.t('mode-static-shape'),
          behavior: this.t('mode-static-behavior'),
          examples: ['DRAFT', 'Public', '42'],
        },
        {
          id: 'expression',
          shape: this.t('mode-expression-shape'),
          behavior: this.t('mode-expression-behavior'),
          examples: ['{{opportunity.name}}', '{{tender.value.amount}}'],
        },
        {
          id: 'template',
          shape: this.t('mode-template-shape'),
          behavior: this.t('mode-template-behavior'),
          examples: ['SEAO: {{tender.source.solicitationNumber}} / {{tender.source.referenceNumber}}'],
        },
        {
          id: 'list-literal',
          shape: this.t('mode-list-literal-shape'),
          behavior: this.t('mode-list-literal-behavior'),
          examples: [
            '{{[tender.source.solicitationNumber, tender.source.referenceNumber]?join= / ?fallback=tender.id}}',
          ],
        },
      ];
    },
    contextRows() {
      return [
        { id: 'opportunity', key: 'opportunity', notes: this.t('context-opportunity') },
        { id: 'tender', key: 'tender', notes: this.t('context-tender') },
        { id: 'currentDate', key: 'currentDate', notes: this.t('context-currentDate') },
        { id: 'alohaTenderUrl', key: 'alohaTenderUrl', notes: this.t('context-alohaTenderUrl') },
        { id: 'tenderChanges', key: 'tenderChanges', notes: this.t('context-tenderChanges') },
        { id: 'existing', key: 'existing.<column>', notes: this.t('context-existing'), bcOnly: true },
      ];
    },
    arrayRows() {
      return [
        {
          id: 'project',
          syntax: '[]',
          behavior: this.t('array-project'),
          example: '{{tender.bids[].tenderer.name}}',
        },
        {
          id: 'index',
          syntax: '[N]',
          behavior: this.t('array-index'),
          example: '{{tender.bids[0].tenderer.name}}',
        },
        {
          id: 'filter-eq',
          syntax: '[k=v]',
          behavior: this.t('array-filter-eq'),
          example: '{{tender.bids[status=submitted].tenderer.name}}',
        },
        {
          id: 'filter-neq',
          syntax: '[k!=v]',
          behavior: this.t('array-filter-neq'),
          example: '{{tender.bids[status!=draft].tenderer.name}}',
        },
        {
          id: 'filter-quoted',
          syntax: "[k='…']",
          behavior: this.t('array-filter-quoted'),
          example: "{{tender.bids[status='in progress'].tenderer.name}}",
        },
        {
          id: 'filter-path',
          syntax: '[a.b.c=v]',
          behavior: this.t('array-filter-path'),
          example: "{{tender.bids[tenderer.address.region='Quebec'].tenderer.name}}",
        },
        {
          id: 'nested',
          syntax: '[].[]',
          behavior: this.t('array-nested'),
          example: '{{tender.awards[].suppliers[].name}}',
        },
      ];
    },
    operatorRows() {
      return [
        {
          id: 'format',
          operator: '?format=PATTERN',
          appliesTo: this.t('type-date'),
          example: '{{tender.publishedDate?format=yyyy-MM-dd}}',
          result: '"2026-05-06"',
        },
        {
          id: 'addDays',
          operator: '?addDays=N',
          appliesTo: this.t('type-date'),
          example: '{{currentDate?addDays=7?format=yyyy-MM-dd}}',
          result: this.t('op-addDays-result'),
        },
        {
          id: 'subtractDays',
          operator: '?subtractDays=N',
          appliesTo: this.t('type-date'),
          example: '{{currentDate?subtractDays=30?format=yyyy-MM-dd}}',
          result: this.t('op-subtractDays-result'),
        },
        {
          id: 'join',
          operator: '?join=DELIM',
          appliesTo: this.t('type-list'),
          example: '{{tender.bids[].tenderer.name?join=; }}',
          result: '"Acme; Beta"',
        },
        {
          id: 'join-quoted',
          operator: "?join='DELIM'",
          appliesTo: this.t('type-list'),
          example: "{{tender.bids[].tenderer.name?join='|'}}",
          result: '"Acme|Beta"',
        },
        {
          id: 'truncate',
          operator: '?truncate=N',
          appliesTo: this.t('type-string'),
          example: '{{tender.description?truncate=2048}}',
          result: this.t('op-truncate-result'),
        },
        {
          id: 'required',
          operator: '?required',
          appliesTo: this.t('type-any'),
          example: '{{tenderChanges[].field?required}}',
          result: this.t('op-required-result'),
        },
        {
          id: 'requiredAny',
          operator: '?requiredAny',
          appliesTo: this.t('type-any'),
          example: '{{a?requiredAny}}',
          result: this.t('op-requiredAny-result'),
        },
        {
          id: 'fallback',
          operator: '?fallback=PATH_OR_LITERAL',
          appliesTo: this.t('type-any'),
          example: '{{tender.value.amount?fallback=0}}',
          result: this.t('op-fallback-result'),
        },
        {
          id: 'nullIf',
          operator: '?nullIf=VALUE_OR_PATH',
          appliesTo: this.t('type-any'),
          example: "{{tender.status?nullIf='N/A'?required}}",
          result: this.t('op-nullIf-result'),
        },
        {
          id: 'nullIfAfter',
          operator: '?nullIfAfter=VALUE_OR_PATH',
          appliesTo: this.t('type-date-or-number'),
          example: '{{tender.publishedDate?nullIfAfter=currentDate?format=yyyy-MM-dd?required}}',
          result: this.t('op-nullIfAfter-result'),
        },
        {
          id: 'nullIfBefore',
          operator: '?nullIfBefore=VALUE_OR_PATH',
          appliesTo: this.t('type-date-or-number'),
          example: '{{tender.tenderPeriod.endDate?nullIfBefore=tender.tenderPeriod.startDate?format=yyyy-MM-dd}}',
          result: this.t('op-nullIfBefore-result'),
        },
      ];
    },
    cookbookRows() {
      return [
        { id: 'simple', pattern: this.t('pattern-simple'), expression: '{{opportunity.name}}' },
        {
          id: 'date',
          pattern: this.t('pattern-date'),
          expression: '{{tender.publishedDate?format=yyyy-MM-dd}}',
        },
        { id: 'static-text', pattern: this.t('pattern-static-text'), expression: 'DRAFT' },
        { id: 'static-number', pattern: this.t('pattern-static-number'), expression: '42' },
        {
          id: 'composed',
          pattern: this.t('pattern-composed'),
          expression: 'SEAO: {{tender.source.solicitationNumber}} / {{tender.source.referenceNumber}}',
        },
        {
          id: 'list-joined',
          pattern: this.t('pattern-list-joined'),
          expression: '{{tender.bids[].tenderer.name}}',
        },
        {
          id: 'list-filtered',
          pattern: this.t('pattern-list-filtered'),
          expression: '{{tender.bids[status=submitted].tenderer.name}}',
        },
        {
          id: 'truncate',
          pattern: this.t('pattern-truncate'),
          expression: '{{tender.description?truncate=2048}}',
        },
        { id: 'today', pattern: this.t('pattern-today'), expression: '{{currentDate?format=yyyy-MM-dd}}' },
        {
          id: 'audit',
          pattern: this.t('pattern-audit'),
          expression:
            '{{existing.modification}}\n{{currentDate?format=yyyy-MM-dd HH:mm}} — {{tenderChanges[].field?required}}',
          explainer: this.t('pattern-audit-explainer'),
          bcOnly: true,
        },
        {
          id: 'tomorrow',
          pattern: this.t('pattern-tomorrow'),
          expression: '{{currentDate?addDays=1?format=yyyy-MM-dd}}',
        },
        {
          id: 'first-award',
          pattern: this.t('pattern-first-award'),
          expression: '{{tender.awards[0].suppliers[0].name}}',
        },
        // ── Fallback patterns ────────────────────────────────────────────────
        {
          id: 'coalesce',
          pattern: this.t('pattern-coalesce'),
          expression: '{{tender.source.solicitationNumber?fallback=tender.source.referenceNumber?fallback=tender.id}}',
          explainer: this.t('pattern-coalesce-explainer'),
        },
        {
          id: 'numeric-fallback',
          pattern: this.t('pattern-numeric-fallback'),
          expression: '{{tender.value.amount?fallback=0}}',
          explainer: this.t('pattern-numeric-fallback-explainer'),
        },
        {
          id: 'list-fallback',
          pattern: this.t('pattern-list-fallback'),
          expression:
            '{{[tender.source.solicitationNumber, tender.source.referenceNumber]?join= / ?fallback=tender.id}}',
          explainer: this.t('pattern-list-fallback-explainer'),
        },
        // ── Null-if / sentinel patterns ──────────────────────────────────────
        {
          id: 'sentinel-date',
          pattern: this.t('pattern-sentinel-date'),
          expression: '{{tender.publishedDate?nullIfAfter=2999-12-31?format=yyyy-MM-dd?required}}',
          explainer: this.t('pattern-sentinel-date-explainer'),
        },
        {
          id: 'end-before-start',
          pattern: this.t('pattern-end-before-start'),
          expression: '{{tender.tenderPeriod.endDate?nullIfBefore=tender.tenderPeriod.startDate?format=yyyy-MM-dd}}',
          explainer: this.t('pattern-end-before-start-explainer'),
        },
        {
          id: 'future-date',
          pattern: this.t('pattern-future-date'),
          expression: '{{tender.publishedDate?nullIfAfter=currentDate?format=yyyy-MM-dd?required}}',
          explainer: this.t('pattern-future-date-explainer'),
        },
      ];
    },
    limitRows() {
      return [
        {
          id: 'numeric-cmp',
          limitation: this.t('limit-numeric-cmp'),
          workaround: this.t('limit-numeric-cmp-workaround'),
        },
        {
          id: 'filter-keys',
          limitation: this.t('limit-filter-keys'),
          workaround: this.t('limit-filter-keys-workaround'),
        },
        {
          id: 'bare-bracket',
          limitation: this.t('limit-bare-bracket'),
          workaround: this.t('limit-bare-bracket-workaround'),
        },
        {
          id: 'bare-question',
          limitation: this.t('limit-bare-question'),
          workaround: this.t('limit-bare-question-workaround'),
        },
        {
          id: 'required-mapping',
          limitation: this.t('limit-required-mapping'),
          workaround: this.t('limit-required-mapping-workaround'),
        },
      ];
    },
  },
  methods: {
    // Shorthand wrapper so the template stays readable.
    t(key) {
      return this.$t(`page.admin-sync-profiles.field-mapping-help.${key}`);
    },
    // Render backtick-fenced fragments in translated prose as inline <code> spans.
    // Translations come from our own locale files — no user input, no XSS concern.
    fmt(text) {
      if (text == null) return '';
      // Escape HTML in the surrounding text, then re-introduce <code> for the fenced parts.
      const escaped = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return escaped.replace(/`([^`]+)`/g, '<code class="ve-help__code-inline">$1</code>');
    },
  },
};
</script>

<style lang="scss">
.ve-help {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: flex-end;

  &__panel {
    width: 680px;
    max-width: 100vw;
    background: $color-white;
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  &__header {
    flex: 0 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 22px 12px;
    border-bottom: 1px solid $color-border;
  }

  &__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $color-normal;
  }

  &__subtitle {
    margin: 4px 0 0;
    font-size: 12px;
    color: $color-normal-pale;
  }

  &__close {
    background: none;
    border: none;
    color: $color-normal-pale;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    transition: color 150ms ease, background 150ms ease;

    .base-icon {
      width: 18px;
      height: 18px;
    }

    &:hover {
      color: $color-normal;
      background: $color-neutral-pale;
    }
  }

  &__body {
    flex: 1 1 auto;
    overflow-y: auto;
    padding: 16px 22px 24px;
  }

  &__section {
    margin-bottom: 28px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__section-title {
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $color-normal-pale;
    margin: 0 0 8px;
  }

  &__intro {
    font-size: 13px;
    color: $color-normal;
    margin: 0 0 10px;
    line-height: 1.5;
  }

  &__note {
    font-size: 12px;
    color: $color-normal-pale;
    margin: 8px 0 0;
    line-height: 1.5;
    font-style: italic;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12.5px;
    line-height: 1.5;
    table-layout: fixed;

    th,
    td {
      text-align: left;
      vertical-align: top;
      padding: 6px 8px;
      border-bottom: 1px solid $color-border;
      word-break: break-word;
    }

    th {
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: $color-normal-pale;
      background: $color-neutral-pale;
    }

    code {
      // override block-display when stacked vertically inside a cell
      display: inline-block;
      margin-right: 4px;
    }
  }

  // The operators table has four columns and the densest content (multi-sentence result
  // text plus inline code chips). Give the prose-heavy "result" column the most room and
  // shrink the short-fixed columns (applies-to, operator label) so cells stop wrapping
  // to vertical noodles.
  &__table--operators {
    th:nth-child(1),
    td:nth-child(1) {
      width: 22%;
    }
    th:nth-child(2),
    td:nth-child(2) {
      width: 12%;
    }
    th:nth-child(3),
    td:nth-child(3) {
      width: 28%;
    }
    th:nth-child(4),
    td:nth-child(4) {
      width: 38%;
    }
  }

  &__code {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    background: $color-neutral-pale;
    color: $color-normal;
    padding: 1px 6px;
    border-radius: 3px;
    border: 1px solid $color-border;
    // Long examples (e.g. {{tender.bids[status='in progress'].tenderer.name}}) need to
    // wrap inside narrow cells, but `break-all` was breaking labels like `PATH_OR_LITERAL`
    // mid-letter. `break-word` only breaks when nothing else fits, and prefers natural
    // boundaries (`_`, `.`, `?`) when it does need to break.
    word-break: break-word;
  }

  // Lighter inline code for technical fragments embedded in prose (?op, paths, etc.).
  // No background or border so it doesn't break the line rhythm; just font + subtle colour.
  &__code-inline {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 0.92em;
    color: $color-normal;
    background: rgba($color-neutral-pale, 0.6);
    padding: 0 4px;
    border-radius: 3px;
    word-break: break-word;
  }

  &__code-block {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12px;
    background: $color-neutral-pale;
    color: $color-normal;
    padding: 8px 10px;
    border-radius: 4px;
    border: 1px solid $color-border;
    margin: 6px 0 0;
    white-space: pre;
    overflow-x: auto;
    line-height: 1.5;
  }

  &__badge {
    display: inline-block;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 10px;
    background: $color-yellow-pale;
    color: $color-yellow;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    vertical-align: middle;
  }

  &__cookbook {
    margin: 0;

    dt {
      font-size: 13px;
      font-weight: 600;
      color: $color-normal;
      margin-top: 12px;

      &:first-child {
        margin-top: 0;
      }
    }

    dd {
      margin: 4px 0 0;
    }
  }

  &__explainer {
    font-size: 12px;
    color: $color-normal-pale;
    line-height: 1.5;
    margin: 6px 0 0;
  }
}

// Slide-in animation: panel from the right, backdrop fade.
.ve-help-enter-active,
.ve-help-leave-active {
  transition: background 180ms ease;

  .ve-help__panel {
    transition: transform 220ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }
}

.ve-help-enter,
.ve-help-leave-to {
  background: rgba(0, 0, 0, 0);

  .ve-help__panel {
    transform: translateX(100%);
  }
}
</style>
